import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { RCAAnalysis as RCAAnalysisType } from '../../types/stats';

interface RCAAnalysisProps {
  data: RCAAnalysisType[];
}

export const RCAAnalysis = ({ data }: RCAAnalysisProps) => {
  const option = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    // Take top 10 RCA codes
    const topRCA = data.slice(0, 10);
    const rcaCodes = topRCA.map((item) => item.rcaCode);
    const counts = topRCA.map((item) => item.count);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const index = params[0].dataIndex;
          const rca = topRCA[index];
          let tooltip = `<strong>${rca.rcaCode}</strong><br/>Count: ${rca.count}<br/>`;
          if (rca.topDefectTypes.length > 0) {
            tooltip += '<br/>Top Defect Types:<br/>';
            rca.topDefectTypes.forEach((defect) => {
              tooltip += `• ${defect.type}: ${defect.count}<br/>`;
            });
          }
          return tooltip;
        },
      },
      grid: { left: 120, right: 30, top: 30, bottom: 60 },
      xAxis: {
        type: 'value',
        name: 'Occurrences',
      },
      yAxis: {
        type: 'category',
        data: rcaCodes,
        axisLabel: { width: 100, overflow: 'truncate' },
      },
      series: [
        {
          type: 'bar',
          data: counts,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#3b82f6' },
              { offset: 1, color: '#8b5cf6' },
            ]),
          },
          label: {
            show: true,
            position: 'right',
            formatter: '{c}',
          },
        },
      ],
    } as echarts.EChartsOption;
  }, [data]);

  if (!option) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No RCA analysis data available.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Root Cause Analysis</h3>
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Top 10 RCA Codes</p>
      </div>
      <ReactECharts 
        option={option} 
        style={{ height: 500 }} 
        key="rca-analysis"
        notMerge={true}
        lazyUpdate={true}
      />
    </div>
  );
};

